// Implements: F-IDN-026, F-IDN-027, F-IDN-009
/**
 * The client of the organization pages (mockup v3 P3): Personas, Equipos, Invitaciones,
 * Plataforma > Organizaciones and the invitation page, on the paths of the P3 contract.
 *
 * Every address is built from the ids the caller passes, each escaped into one path segment. The
 * server decides every access; a refusal arrives as the `ApiError` of `httpClient`, its message the
 * server's own (a `409` says why in Spanish). Every write is sent **at most once** (`retry: false`,
 * checklist 13): a lost answer is shown as a failure the person may repeat, never repeated for them.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import { organizationPeoplePath } from "../../../shared/api/people";
import type { UserSummary } from "../../../types/api";
import type {
  InvitationAcceptRequest,
  InvitationList,
  InvitationRequest,
  IssuedInvitation,
  OrganizationList,
  OrganizationStatus,
  OrganizationView,
  OrgRole,
  PeopleList,
  PersonView,
  ReassignCandidates,
  ReassignResult,
  TeamList,
  TeamRole,
  TeamSeat,
  TeamView,
} from "../../../types/organization";

const ORGANIZATIONS = `${API_BASE}/organizations`;

function organization(organizationId: string): string {
  return `${ORGANIZATIONS}/${encodeURIComponent(organizationId)}`;
}

function team(organizationId: string, teamId: string): string {
  return `${organization(organizationId)}/teams/${encodeURIComponent(teamId)}`;
}

function invitation(organizationId: string, invitationId: string): string {
  return `${organization(organizationId)}/invitations/${encodeURIComponent(invitationId)}`;
}

/** A read or write whose body is missing is a broken answer, not an empty one. */
function required<T>(body: T | undefined, path: string): T {
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

async function read<T>(path: string, signal?: AbortSignal): Promise<T> {
  return required(await httpRequest<T>(path, { signal }), path);
}

// One helper per method, each naming its method literally: the screen-coverage gate reads the
// method of every call from the source (P10).
async function post<T>(path: string, body?: unknown): Promise<T> {
  return required(await httpRequest<T>(path, { method: "POST", body, retry: false }), path);
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  return required(await httpRequest<T>(path, { method: "PATCH", body, retry: false }), path);
}

async function remove(path: string): Promise<void> {
  await httpRequest(path, { method: "DELETE", retry: false });
}

// --- Personas -----------------------------------------------------------------------------

export function fetchPeople(organizationId: string, signal?: AbortSignal): Promise<PeopleList> {
  return read<PeopleList>(organizationPeoplePath(organizationId), signal);
}

export function setPersonRole(organizationId: string, userId: string, role: OrgRole): Promise<PersonView> {
  return patch<PersonView>(`${organization(organizationId)}/people/${encodeURIComponent(userId)}`, { role });
}

export function removePerson(organizationId: string, userId: string): Promise<void> {
  return remove(`${organization(organizationId)}/people/${encodeURIComponent(userId)}`);
}

/**
 * Who can receive the mockups `userId` owns in this organization (owner 2026-10-19): the server asks
 * the rule `POST /mockups/reassign` refuses with, so only people it would accept are offered.
 */
export function fetchReassignCandidates(
  organizationId: string,
  userId: string,
  signal?: AbortSignal,
): Promise<ReassignCandidates> {
  return read<ReassignCandidates>(
    `${organization(organizationId)}/people/${encodeURIComponent(userId)}/reassign-candidates`,
    signal,
  );
}

/**
 * `POST /mockups/reassign` for one organization: every mockup `fromUserId` owns there (its scope and
 * its teams', trash included) moves to `toUserId`, all or nothing. Their personal mockups and those
 * of other organizations stay with them (Default P3-5).
 */
export function reassignMockups(organizationId: string, fromUserId: string, toUserId: string): Promise<ReassignResult> {
  return post<ReassignResult>(`${API_BASE}/mockups/reassign`, {
    from_user_id: fromUserId,
    to_user_id: toUserId,
    organization_id: organizationId,
  });
}

// --- Equipos ------------------------------------------------------------------------------

/** A team as a server from before P3 lists it: without members. */
type WireTeam = Omit<TeamView, "members"> & { readonly members?: TeamView["members"] };

function withMembers(entry: WireTeam): TeamView {
  return { ...entry, members: entry.members ?? [] };
}

export async function fetchTeams(organizationId: string, signal?: AbortSignal): Promise<TeamList> {
  const list = await read<{ readonly teams: readonly WireTeam[] }>(`${organization(organizationId)}/teams`, signal);
  // A server from before P3 lists teams without members: read that as a team with nobody.
  return { teams: list.teams.map(withMembers) };
}

export async function createTeam(organizationId: string, name: string): Promise<TeamView> {
  return withMembers(await post<WireTeam>(`${organization(organizationId)}/teams`, { name }));
}

export function renameTeam(organizationId: string, teamId: string, name: string): Promise<TeamView> {
  return patch<TeamView>(team(organizationId, teamId), { name });
}

export function deleteTeam(organizationId: string, teamId: string): Promise<void> {
  return remove(team(organizationId, teamId));
}

/**
 * One leader per team (P3-3, owner 2026-10-19): a lead request onto a team led by somebody else is
 * `409 lead_taken` unless it carries `replace_lead_user_id`, the lead the question named, sent only
 * after the person chose "Convertir a <name> en miembro". The server demotes that person only while
 * they still lead the team. Otherwise the key is left out.
 */
function handOver(replaceLeadUserId: string | undefined): { readonly replace_lead_user_id?: string } {
  return replaceLeadUserId === undefined ? {} : { replace_lead_user_id: replaceLeadUserId };
}

export function addTeamMember(
  organizationId: string,
  teamId: string,
  userId: string,
  role: TeamRole,
  replaceLeadUserId?: string,
): Promise<TeamSeat> {
  return post<TeamSeat>(`${team(organizationId, teamId)}/members`, { user_id: userId, role, ...handOver(replaceLeadUserId) });
}

export function setTeamMemberRole(
  organizationId: string,
  teamId: string,
  userId: string,
  role: TeamRole,
  replaceLeadUserId?: string,
): Promise<TeamSeat> {
  return patch<TeamSeat>(`${team(organizationId, teamId)}/members/${encodeURIComponent(userId)}`, {
    role,
    ...handOver(replaceLeadUserId),
  });
}

export function removeTeamMember(organizationId: string, teamId: string, userId: string): Promise<void> {
  return remove(`${team(organizationId, teamId)}/members/${encodeURIComponent(userId)}`);
}

// --- Invitaciones -------------------------------------------------------------------------

export function fetchInvitations(organizationId: string, signal?: AbortSignal): Promise<InvitationList> {
  return read<InvitationList>(`${organization(organizationId)}/invitations`, signal);
}

export function issueInvitation(organizationId: string, request: InvitationRequest): Promise<IssuedInvitation> {
  return post<IssuedInvitation>(`${organization(organizationId)}/invitations`, request);
}

/** A new token for the invitation; the previous one stops working (C5). No body. */
export function rotateInvitationLink(organizationId: string, invitationId: string): Promise<IssuedInvitation> {
  return post<IssuedInvitation>(`${invitation(organizationId, invitationId)}/link`);
}

export function revokeInvitation(organizationId: string, invitationId: string): Promise<void> {
  return remove(invitation(organizationId, invitationId));
}

/** `POST /auth/invitations/accept`: creates the person and signs them in (F-IDN-009). */
export function acceptInvitation(request: InvitationAcceptRequest): Promise<UserSummary> {
  return post<UserSummary>(`${API_BASE}/auth/invitations/accept`, request);
}

// --- Plataforma > Organizaciones ----------------------------------------------------------

export function fetchOrganizations(signal?: AbortSignal): Promise<OrganizationList> {
  return read<OrganizationList>(ORGANIZATIONS, signal);
}

export function createOrganization(name: string): Promise<OrganizationView> {
  return post<OrganizationView>(ORGANIZATIONS, { name });
}

export function renameOrganization(organizationId: string, name: string): Promise<OrganizationView> {
  return patch<OrganizationView>(organization(organizationId), { name });
}

export function setOrganizationStatus(organizationId: string, status: OrganizationStatus): Promise<OrganizationView> {
  return patch<OrganizationView>(`${organization(organizationId)}/status`, { status });
}
